/**
 * types
 */

import type { Script, ScriptProps } from 'commons/vo/Script/type';
import KeyFlowVO from 'commons/vo/KeyFlowVO';

/**
 * Constants
 */
import { STORAGE_URL, SystemFolder } from 'commons/constants/KeyFlowConstants';
import HostFactory from 'commons/handler/HostFactory';

/**
 * Utils
 */
import TimeCode from 'commons/utils/TimeCode';
import { assetWithToken } from 'commons/utils/commonUtil';

/**
 * Image
 */
import DefaultThumbnail from 'asset/thumbnail/thumb_default.jpg';
import ScriptDefaultPlayerIcon from 'asset/images/icon_script_default_player.png';

class ScriptImpl extends KeyFlowVO implements Script {
  id: number;
  categoryId: number;
  script: string;
  owner: number;
  videoId: number;
  inPoint: number;
  outPoint: number;
  private _thumbnailPath: string;
  thumbnailFrame: number;
  downloadCount: number;
  rating: number;
  dateOfCreated: string;
  dateOfModified: string;
  inTimecodeUtil: TimeCode | null;
  outTimecodeUtil: TimeCode | null;
  quarter: string;
  gameTime: string;
  score: string;
  type: string;
  constructor(data: ScriptProps) {
    super(data);
    this.id = data.ID;
    this.categoryId = data.CATEGORY_ID;
    this.script = data.SCRIPT;
    this.owner = data.OWNER;
    this.videoId = data.VIDEO_ID;
    this.inPoint = data.IN_POINT;
    this.outPoint = data.OUT_POINT;
    this._thumbnailPath = data.THUMBNAIL_PATH || '';
    this.thumbnailFrame = data.THUMBNAIL_FRAME;
    this.downloadCount = data.DOWNLOAD_COUNT;
    this.rating = data.RATING;
    this.dateOfCreated = data.DATE_OF_CREATED;
    this.dateOfModified = data.DATE_OF_MODIFIED;
    this.quarter = data.QUARTER;
    this.gameTime = data.GAME_TIME;
    this.score = data.SCORE;
    this.inTimecodeUtil = null;
    this.outTimecodeUtil = null;
    this.type = data.TYPE;
  }

  startWithSlash(value?: string) {
    let result = false;

    if (value) result = value.trim().startsWith('/');

    return result;
  }

  get thumbnailPath() {
    const { _thumbnailPath } = this;
    const path = _thumbnailPath || '';
    const hasSlash = this.startWithSlash(path);

    return hasSlash ? `${path}` : `/${path}`;
  }

  getThumbnailBackgroundURL(symbolicPath: string) {
    const cloudfront = `${HostFactory.cloudfrontURL}/${SystemFolder.SCRIPT_FOLDER_NAME}/${this.categoryId}/${this.videoId}${this.thumbnailPath}`;
    const nas = this.getThumbnailNasPath(symbolicPath);
    return `url(${cloudfront}), url(${nas}), url(${ScriptDefaultPlayerIcon})`;
  }

  set fps(fps: number) {
    const inTimecodeUtil = new TimeCode(fps);
    const outTimecodeUtil = new TimeCode(fps);

    inTimecodeUtil.setFrames(this.inPoint);
    outTimecodeUtil.setFrames(this.outPoint);

    this.inTimecodeUtil = inTimecodeUtil;
    this.outTimecodeUtil = outTimecodeUtil;
  }

  get inTime() {
    const { inTimecodeUtil } = this;
    return inTimecodeUtil ? inTimecodeUtil.getSeconds() : 0;
  }

  get outTime() {
    const { outTimecodeUtil } = this;
    return outTimecodeUtil ? outTimecodeUtil.getSeconds() : 0;
  }

  get inTimecode() {
    const { inTimecodeUtil } = this;
    return inTimecodeUtil ? inTimecodeUtil.getTimeCode() : '-';
  }

  get outTimecode() {
    const { outTimecodeUtil } = this;
    return outTimecodeUtil ? outTimecodeUtil.getTimeCode() : '-';
  }

  getThumbnailNasPath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${SystemFolder.SCRIPT_FOLDER_NAME}/${this.categoryId}/${this.videoId}${this.thumbnailPath}`;
    return assetWithToken(url);
  }
}

export default ScriptImpl;
